import { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '../stores/authStore';
import { API_BASE_URL } from '../config/api';

interface PlatformRate {
  base: number;
  perKm: number;
  perMin: number;
  minFare: number;
}
interface Pricing {
  platforms: Record<string, PlatformRate>;
  surcharges: {
    airport: number;
    nightMultiplier: number;
    sundayMultiplier: number;
    peakMultiplier: number;
    weekendNightMultiplier: number;
  };
}

const DEFAULTS: Pricing = {
  platforms: {
    uber:     { base: 4500, perKm: 900, perMin: 170, minFare: 9000 },
    didi:     { base: 3200, perKm: 600, perMin: 105, minFare: 7000 },
    indriver: { base: 3000, perKm: 560, perMin: 110, minFare: 6000 },
    cabify:   { base: 3000, perKm: 595, perMin: 180, minFare: 3500 },
    yango:    { base: 3500, perKm: 680, perMin: 120, minFare: 7500 },
    taxislibres: { base: 3200, perKm: 1200, perMin: 110, minFare: 6500 },
  },
  surcharges: {
    airport: 8000,
    nightMultiplier: 1.20,
    sundayMultiplier: 1.20,
    peakMultiplier: 1.12,
    weekendNightMultiplier: 1.30,
  },
};

const PLATFORM_LABELS: Record<string, { name: string; emoji: string }> = {
  uber: { name: 'Uber', emoji: '🚗' },
  didi: { name: 'DiDi', emoji: '🟠' },
  indriver: { name: 'InDriver', emoji: '🟢' },
  cabify: { name: 'Cabify', emoji: '🟣' },
  yango: { name: 'Yango', emoji: '🔴' },
  taxislibres: { name: 'Taxis Libres', emoji: '🚕' },
};

const PLATFORM_IDS = ['uber', 'didi', 'indriver', 'cabify', 'yango', 'taxislibres'];

export default function PricingConfig() {
  const { token } = useAuthStore();
  const [pricing, setPricing] = useState<Pricing>(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/admin/pricing`);
      if (res.ok) {
        const data = await res.json();
        if (data?.pricing?.platforms && data?.pricing?.surcharges) {
          setPricing(data.pricing);
        }
      }
    } catch { /* usa defaults */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updatePlatform = (id: string, field: keyof PlatformRate, value: string) => {
    setPricing((prev) => ({
      ...prev,
      platforms: {
        ...prev.platforms,
        [id]: { ...prev.platforms[id], [field]: Number(value) || 0 },
      },
    }));
  };

  const updateSurcharge = (field: keyof Pricing['surcharges'], value: string) => {
    setPricing((prev) => ({
      ...prev,
      surcharges: { ...prev.surcharges, [field]: Number(value) || 0 },
    }));
  };

  const save = async () => {
    if (!token) { setMessage('No hay sesión de administrador.'); return; }
    setSaving(true);
    setMessage('');
    try {
      const res = await fetch(`${API_BASE_URL}/admin/pricing`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ pricing }),
      });
      if (res.ok) {
        setMessage('✅ Tarifas guardadas. Los cambios se aplican de inmediato en la app y la web.');
      } else {
        const err = await res.json().catch(() => ({}));
        setMessage(`❌ Error: ${err.message || err.error || res.status}`);
      }
    } catch {
      setMessage('❌ Error de conexión al guardar.');
    } finally {
      setSaving(false);
    }
  };

  const resetDefaults = () => {
    setPricing(DEFAULTS);
    setMessage('Valores por defecto cargados. Recuerda hacer clic en Guardar para aplicarlos.');
  };

  if (loading) {
    return <div className="bg-white rounded-xl shadow-md p-6 text-gray-500">Cargando tarifas...</div>;
  }

  return (
    <div className="bg-white rounded-xl shadow-md p-6">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-xl font-bold text-gray-900">💰 Configuración de Tarifas</h2>
        <button onClick={resetDefaults} className="text-sm text-gray-500 hover:text-blue-600 underline">
          Restaurar valores por defecto
        </button>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Ajusta las tarifas de cada plataforma. Los valores están en pesos colombianos (COP).
        Fórmula: <span className="font-mono text-xs">(base + perKm×km + perMin×min) × recargo + aeropuerto</span>
      </p>

      {/* Tabla de tarifas por plataforma */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 text-left text-gray-600">
              <th className="py-2 pr-4">Plataforma</th>
              <th className="py-2 px-2">Base (COP)</th>
              <th className="py-2 px-2">Por km</th>
              <th className="py-2 px-2">Por min</th>
              <th className="py-2 px-2">Tarifa mínima</th>
            </tr>
          </thead>
          <tbody>
            {PLATFORM_IDS.map((id) => {
              const rate = pricing.platforms[id] || DEFAULTS.platforms[id];
              const label = PLATFORM_LABELS[id];
              return (
                <tr key={id} className="border-b border-gray-100">
                  <td className="py-2 pr-4 font-medium whitespace-nowrap">
                    {label.emoji} {label.name}
                  </td>
                  {(['base', 'perKm', 'perMin', 'minFare'] as (keyof PlatformRate)[]).map((field) => (
                    <td key={field} className="py-2 px-2">
                      <input
                        type="number"
                        value={rate[field]}
                        onChange={(e) => updatePlatform(id, field, e.target.value)}
                        className="w-24 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Recargos */}
      <h3 className="font-bold text-gray-900 mt-6 mb-3">Recargos y multiplicadores</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm text-gray-600 mb-1">Recargo aeropuerto (COP)</label>
          <input type="number" value={pricing.surcharges.airport}
            onChange={(e) => updateSurcharge('airport', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500" />
        </div>
        <div>
          <label className="block text-sm text-gray-600 mb-1">Multiplicador nocturno (desde 8 PM)</label>
          <input type="number" step="0.05" value={pricing.surcharges.nightMultiplier}
            onChange={(e) => updateSurcharge('nightMultiplier', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500" />
        </div>
        <div>
          <label className="block text-sm text-gray-600 mb-1">Multiplicador dominical/festivo</label>
          <input type="number" step="0.05" value={pricing.surcharges.sundayMultiplier}
            onChange={(e) => updateSurcharge('sundayMultiplier', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500" />
        </div>
        <div>
          <label className="block text-sm text-gray-600 mb-1">Multiplicador hora pico</label>
          <input type="number" step="0.05" value={pricing.surcharges.peakMultiplier}
            onChange={(e) => updateSurcharge('peakMultiplier', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500" />
        </div>
        <div>
          <label className="block text-sm text-gray-600 mb-1">Multiplicador viernes/sábado noche</label>
          <input type="number" step="0.05" value={pricing.surcharges.weekendNightMultiplier}
            onChange={(e) => updateSurcharge('weekendNightMultiplier', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500" />
        </div>
      </div>

      {message && (
        <div className="mt-4 text-sm p-3 rounded-lg bg-blue-50 text-blue-800">{message}</div>
      )}

      <div className="mt-6 flex gap-3">
        <button onClick={save} disabled={saving}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium">
          {saving ? 'Guardando...' : '💾 Guardar tarifas'}
        </button>
        <button onClick={load} disabled={saving}
          className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">
          Recargar
        </button>
      </div>
    </div>
  );
}
